import type { MillageInputsForm } from '~/builders/millage-info/MillageInfoInputs';
import { getMillagePhaseForYear, getTransitionPeriodInfo } from '../../utilities/millage-phase';
import type { LogAccelBuild } from './shape';
import { calculateLogAccel } from './shape';

interface GenerateShapeParams {
  build: LogAccelBuild;
  millageInputs: MillageInputsForm;
  startYear: number;
  endYear: number;
}

export function generateLogAccelShape({
  build,
  millageInputs,
  startYear,
  endYear,
}: GenerateShapeParams): Record<number, { taxYear: number; millage: number; isTunable: boolean }> {
  const { interval } = build;
  const { a, b } = interval;
  const totalYears = endYear - startYear + 1;

  // Calculate the total drop in the function's range
  const startValue = calculateLogAccel(a);
  const endValue = calculateLogAccel(b);
  const totalDrop = startValue - endValue;

  // Get transition period info
  const transitionInfo = getTransitionPeriodInfo(startYear, totalYears, millageInputs);

  // Generate millage values for each tax year
  const result: Record<number, { taxYear: number; millage: number; isTunable: boolean }> = {};
  const years = Array.from({ length: totalYears }, (_, i) => startYear + i);

  years.forEach((year) => {
    const phaseInfo = getMillagePhaseForYear(year, startYear, totalYears, millageInputs);

    // If we're in peak or minimum phase, use the provided millage value
    if (phaseInfo.phase !== 'transition') {
      result[year] = {
        taxYear: year,
        millage: phaseInfo.millage,
        isTunable: false,
      };
      return;
    }

    // Calculate transition millage using logarithmic acceleration
    const yearsIntoTransition = year - transitionInfo.startYear;

    // Avoid division by zero if transition period is only one year long
    if (transitionInfo.length <= 1) {
      result[year] = {
        taxYear: year,
        millage: transitionInfo.peakMillage,
        isTunable: true,
      };
      return;
    }

    // Calculate the position in the function's domain
    const transitionProgress = yearsIntoTransition / (transitionInfo.length - 1);
    const x = a + transitionProgress * (b - a);

    // Calculate the function value and normalize it
    const functionValue = calculateLogAccel(x);
    const normalizedValue = (startValue - functionValue) / totalDrop;

    // Apply to millage range
    const millageRange = transitionInfo.peakMillage - transitionInfo.minimumMillage;
    const millage = transitionInfo.peakMillage - normalizedValue * millageRange;

    const roundedMillage = Math.round(millage * 100) / 100;

    result[year] = {
      taxYear: year,
      millage: roundedMillage,
      isTunable: true,
    };
  });

  return result;
}
